import {inject, injectable} from 'inversify';

import type {ConferenceApiModel} from '../../../../api/conference';
import type {IApiDataMapper} from '../../../ports/types';
import type {ConferenceModel} from '../Conference.model';

import {
  getConferenceTitle,
  isConferenceStarted,
} from '../../../../utils/entity/conference';
import {isDefined} from '../../../../utils/typecheck';
import {CourseMapperJrpc} from '../../Course/mappers/Course.mapper.jrpc';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {ConferenceFactory} from '../Conference.factory';
import {ConferenceExpandJrpc} from './Conference.expand.jrpc';

@injectable()
export class ConferenceMapperJrpc
  implements
    IApiDataMapper<
      ConferenceMapperJrpc.ApiData,
      ConferenceModel.Props,
      ConferenceModel
    >
{
  constructor(
    @inject(ConferenceFactory) private readonly factory: ConferenceFactory,
    @inject(ConferenceExpandJrpc) public readonly expand: ConferenceExpandJrpc,
    @inject(CourseMapperJrpc) private readonly courseMapper: CourseMapperJrpc,
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
  ) {}

  mapDataToModel(
    apiData: ConferenceMapperJrpc.ExpandedApiData,
  ): ConferenceModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(
    apiData: ConferenceMapperJrpc.ExpandedApiData,
  ): ConferenceModel.Props {
    const id = apiData._id;
    const type = this.type(apiData);
    const status = this.status(apiData);
    const description = this.description(apiData);
    const isChatConference = this.isChatConference(apiData);
    const isInCourse = this.isInCourse(apiData);
    const isCreatedByClient = this.isCreatedByClient(apiData);
    const channelId = this.channelId(apiData);
    const isStarted = this.isStarted({apiData, isChatConference});
    const isShowJoinButton = isStarted;
    const title = this.title({
      apiData,
      isChatConference,
      isCreatedByClient,
      isInCourse,
    });
    const course = this.course(apiData);
    const startDate = this.startDate(apiData);
    const endDate = this.endDate(apiData);

    return {
      apiData: {...apiData},
      channelId,
      course,
      description,
      endDate,
      id,
      isChatConference,
      isCreatedByClient,
      isInCourse,
      isJoinAvailable: isShowJoinButton,
      isStarted,
      startDate,
      status,
      title,
      type,
    };
  }

  private channelId(
    props: ConferenceMapperJrpc.ExpandedApiData,
  ): ConferenceModel['channelId'] {
    return props.course?.channel_ids[0] ?? null;
  }

  private course(
    props: ConferenceMapperJrpc.ExpandedApiData,
  ): ConferenceModel['course'] {
    const course = props.course;

    if (!course) {
      return null;
    }

    return this.courseMapper.mapDataToModel(course);
  }

  private description(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['description'] {
    return props.description;
  }

  private endDate(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['endDate'] {
    return this.dateTimeModelFactory.createFromDateTimeObject(props.end_date);
  }

  private isChatConference(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['isChatConference'] {
    return props.type === 'chat';
  }

  private isCreatedByClient(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['isCreatedByClient'] {
    return props.created_by === 'client';
  }

  private isInCourse(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['isInCourse'] {
    return isDefined(props.course_id);
  }

  private isStarted(props: {
    apiData: ConferenceMapperJrpc.ApiData;
    isChatConference: boolean;
  }): ConferenceModel['isStarted'] {
    // const {apiData, isChatConference} = props;
    return isConferenceStarted(props.apiData);
  }

  private startDate(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['startDate'] {
    return this.dateTimeModelFactory.createFromDateTimeObject(props.start_date);
  }

  private status(
    props: ConferenceMapperJrpc.ApiData,
  ): ConferenceModel['status'] {
    return props.status;
  }

  private title(props: {
    apiData: ConferenceMapperJrpc.ApiData;
    isChatConference: boolean;
    isCreatedByClient: boolean;
    isInCourse: boolean;
  }): ConferenceModel['title'] {
    // const {isChatConference, isInCourse, isCreatedByClient, apiData} = props;

    return getConferenceTitle(props.apiData);
  }

  private type(props: ConferenceMapperJrpc.ApiData): ConferenceModel['type'] {
    return props.type ?? null;
  }
}

export namespace ConferenceMapperJrpc {
  export type ApiData = ConferenceApiModel;

  export type ExpandedApiData = ApiData & ConferenceExpandJrpc.ExpandedFields;
}
